export interface BenchLocation {
  admissionNo: string;
  wardId: string | null;
  wardName: string | null;
  roomCode: string | null;
  bedCode: string | null;
}

export interface GroupableRow {
  id: string;
  accessionNo: string | null;
  priority: string;
  statOverdue?: boolean;
  collectAt?: string | null;
  location?: BenchLocation | null;
  test: { name: string; specimenType: string; containerType: string | null };
  patient: { id: string; fullName: string };
}

export interface TubeGroup<T extends GroupableRow> {
  key: string;
  patientKey: string;
  firstOfPatient: boolean;
  specimen: string;
  container: string | null;
  accessionNo: string | null;
  priority: string;
  statOverdue: boolean;
  collectAt: string | null;
  rows: T[];
}

const PRIORITY_RANK: Record<string, number> = { stat: 0, urgent: 1, routine: 2 };

function tubeKeyOf(row: GroupableRow): string {
  return `${row.test.specimenType.trim().toLowerCase()}|${(row.test.containerType ?? '').trim().toLowerCase()}`;
}

export function groupTubes<T extends GroupableRow>(rows: T[]): TubeGroup<T>[] {
  const patientOrder: string[] = [];
  const byPatient = new Map<string, Map<string, TubeGroup<T>>>();
  for (const row of rows) {
    const patientKey = row.patient.id;
    let tubes = byPatient.get(patientKey);
    if (!tubes) {
      tubes = new Map();
      byPatient.set(patientKey, tubes);
      patientOrder.push(patientKey);
    }
    const key = `${patientKey}|${row.accessionNo ?? tubeKeyOf(row)}`;
    const tube = tubes.get(key) ?? {
      key,
      patientKey,
      firstOfPatient: false,
      specimen: row.test.specimenType,
      container: row.test.containerType,
      accessionNo: row.accessionNo,
      priority: row.priority,
      statOverdue: false,
      collectAt: row.collectAt ?? null,
      rows: [],
    };
    tube.rows.push(row);
    if ((PRIORITY_RANK[row.priority] ?? 3) < (PRIORITY_RANK[tube.priority] ?? 3)) tube.priority = row.priority;
    if (row.statOverdue) tube.statOverdue = true;
    if (row.collectAt && (!tube.collectAt || row.collectAt < tube.collectAt)) tube.collectAt = row.collectAt;
    tubes.set(key, tube);
  }
  const groups: TubeGroup<T>[] = [];
  for (const patientKey of patientOrder) {
    const tubes = [...(byPatient.get(patientKey) as Map<string, TubeGroup<T>>).values()];
    tubes.forEach((tube, index) => groups.push({ ...tube, firstOfPatient: index === 0 }));
  }
  return groups;
}

export function locationText(location: BenchLocation | null | undefined): string | null {
  if (!location) return null;
  const parts = [location.wardName, location.roomCode, location.bedCode].filter(Boolean);
  return parts.length ? parts.join(' · ') : location.admissionNo;
}

export const SOURCE_LABELS: Record<string, string> = {
  opd: 'OPD',
  ipd: 'Ward',
  er: 'ER',
  walkin: 'Walk-in',
  home: 'Home',
};

export function verifiableItems<T extends { id: string; status: string; latestResult?: { enteredBy: string | null } | null }>(
  items: T[],
  options: { separateVerifier: boolean; userId: string | null },
): { ready: T[]; enteredByMe: T[] } {
  const resulted = items.filter((item) => item.status === 'resulted');
  const enteredByMe = options.separateVerifier && options.userId ? resulted.filter((item) => item.latestResult?.enteredBy === options.userId) : [];
  const mine = new Set(enteredByMe.map((item) => item.id));
  return { ready: resulted.filter((item) => !mine.has(item.id)), enteredByMe };
}
